/**
 * Wizard OAuth State Management
 *
 * Preserves wizard state across OAuth redirects using sessionStorage.
 * Used when user initiates Shopify or Meta OAuth from the SetupWizard.
 *
 * Why sessionStorage (not Zustand)?
 * - OAuth redirect state is transient security context, not application state
 * - sessionStorage is tab-scoped (isolated) and auto-clears on tab close
 * - Industry standard for OAuth libraries (auth0-spa-js, next-auth, oidc-client)
 */

const WIZARD_OAUTH_KEY = 'wizard_oauth_state';

// 10 minutes - matches backend CSRF state TTL
const STATE_TTL_MS = 10 * 60 * 1000;

export type WizardOAuthState = {
  returnUrl: string;
  shopifyConnected: boolean;
  shopifyConnectionId: string | null;
  metaConnected?: boolean;
  metaConnectionId?: string | null;
  oauthType?: 'shopify' | 'meta';
  timestamp: number;
};

/**
 * Save wizard state before OAuth redirect
 */
export function saveWizardOAuthState(
  state: Omit<WizardOAuthState, 'timestamp'>
): void {
  const stateWithTimestamp: WizardOAuthState = {
    ...state,
    timestamp: Date.now(),
  };
  sessionStorage.setItem(WIZARD_OAUTH_KEY, JSON.stringify(stateWithTimestamp));
}

/**
 * Retrieve wizard state after OAuth callback
 * Returns null if state doesn't exist or has expired (>10 min)
 */
export function getWizardOAuthState(): WizardOAuthState | null {
  const stored = sessionStorage.getItem(WIZARD_OAUTH_KEY);
  if (!stored) return null;

  try {
    const state = JSON.parse(stored) as WizardOAuthState;

    // Check for expiry
    if (Date.now() - state.timestamp > STATE_TTL_MS) {
      clearWizardOAuthState();
      return null;
    }

    return state;
  } catch (error) {
    // Log for debugging while still handling gracefully
    console.warn('Failed to parse wizard OAuth state, clearing:', error);
    clearWizardOAuthState();
    return null;
  }
}

/**
 * Clear wizard state (call after processing OAuth return)
 */
export function clearWizardOAuthState(): void {
  sessionStorage.removeItem(WIZARD_OAUTH_KEY);
}
